import type { HTMLAttributes, ReactNode } from 'react'

import { clsx } from 'clsx'

import styles from './ReplyFormItem.module.css'

type ReplyFormItemElement = 'div' | 'section'

interface ReplyFormItemProps extends Omit<HTMLAttributes<HTMLElement>, 'as'> {
  /**
   * The HTML element type to render. Defaults to 'div'.
   */
  as?: ReplyFormItemElement
  /**
   * Node rendered in the left column – the avatar.
   */
  avatar: ReactNode
  /**
   * Node rendered above the main content - suggestions.
   */
  suggestions?: ReactNode
  /**
   * Children rendered in the right (content) column.
   */
  children: ReactNode
  /**
   * Whether suggestions are visible.
   */
  hasSuggestions?: boolean
}

export const ReplyFormItem = ({
  as: Component = 'div',
  avatar,
  suggestions,
  children,
  hasSuggestions = false,
  className,
  ...props
}: ReplyFormItemProps): JSX.Element => {
  return (
    <Component
      {...props}
      className={clsx(styles.container, className)}
      data-has-suggestions={hasSuggestions}
      data-testid="reply-form-item"
    >
      {hasSuggestions && suggestions && (
        <div className={styles.suggestions}>{suggestions}</div>
      )}

      <div className={styles.mainContent}>
        <aside className={styles.avatar}>{avatar}</aside>
        <div className={styles.content}>{children}</div>
      </div>
    </Component>
  )
}

ReplyFormItem.displayName = 'ReplyFormItem'
